import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DataService } from '@app-core/services/data/data.service';
import { VIDEO_FORMATS, DVR_RESOLUTION_OPTIONS } from '@app-core/constants/constants';
import { DvrService } from '@app-dvr/services/dvr/dvr.service';
import { CreateLocalDVRRequestBody } from '@app-dvr/common/dvr.model';
import { TranslateService } from '@ngx-translate/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { Subject } from 'rxjs';
import { AccessService } from '@app-core/services/access/access.service';
import { MatSelectChange } from '@angular/material/select';

interface QueryParams {
  uploadRequestId: String;
}

const RESOLUTION_1080 = '1920x1080';

@Component({
  selector: 'app-local-dvr',
  templateUrl: './local-dvr.component.html',
  styleUrls: ['./local-dvr.component.scss'],
})
export class LocalDvrComponent implements OnInit {
  public videoRequestDetailsLoader: boolean = false;
  public isSuccess = false;
  public isError = false;
  public loader = false;
  public videoRequestDetails: any;
  public videoResolution: any;
  public videoTypeConfig: any;
  public selectedVideoType: string;
  public selectedVideoResolution: string;
  public uploadRequestId: any;
  public duration;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    public dataService: DataService,
    public dvrService: DvrService,
    private snackBarService: SnackBarService,
    public translate: TranslateService,
    private accessService: AccessService
  ) {}

  ngOnInit(): void {
    this.videoRequestDetails = this.dvrService?.localDVRDetails;

    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: QueryParams) => {
      const { uploadRequestId } = params;
      this.uploadRequestId = uploadRequestId;
    });
    this.videoResolution = DVR_RESOLUTION_OPTIONS;
    this.videoTypeConfig = VIDEO_FORMATS;
    const { endTime, startTime } = this.videoRequestDetails;
    const startTimeDate = new Date(startTime);
    const endTimeDate = new Date(endTime);
    const durationInMilliseconds = endTimeDate.getTime() - startTimeDate.getTime();
    this.duration = durationInMilliseconds / 1000;
  }

  public navigateBack() {
    this.location.back();
  }

  public createLocalDVRRequest() {
    this.isSuccess = false;
    this.isError = false;
    this.loader = true;
    const uploadRequestId = this.uploadRequestId;
    let unitSystem = '';
    if (this.dataService._currentMetricUnit.value === 'Kilometres') {
      //send metric
      unitSystem = 'metric';
    } else {
      unitSystem = 'imperial';
    }

    const { loginName: userEmail, name: userName } = this.accessService.getLoginInfo();
    const dvrVideoType = this.selectedVideoType || 'road';
    const videoResolution = this.selectedVideoResolution || '640x360';
    let dvrVideoTypeMainFrame = 'road';
    if (dvrVideoType === 'pictureInPictureRoadSide') {
      dvrVideoTypeMainFrame = 'road';
    } else if (dvrVideoType === 'pictureInPictureDriverSide') {
      dvrVideoTypeMainFrame = 'driver';
    }

    const body = new CreateLocalDVRRequestBody({
      uploadRequestId,
      unitSystem,
      videoResolution,
      dvrVideoType:
        dvrVideoType === 'pictureInPictureRoadSide' || dvrVideoType === 'pictureInPictureDriverSide' ? 'pictureInPicture' : dvrVideoType,
      dvrVideoTypeMainFrame,
      dvrInitiatedMetadata: {
        userType: 'FLEET_MANAGER',
        email: userEmail,
        name: userName,
      },
    });
    this.dvrService
      .createLocalDvrRequest(body)
      .pipe(
        finalize(() => {
          this.loader = false;
        })
      )
      .subscribe(
        () => {
          this.isSuccess = true;
          this.snackBarService.success(this.translate.instant('Video Requested Successfully'));
        },
        () => {
          this.isError = true;
          this.snackBarService.failure(this.translate.instant('Video Request Failed'));
        }
      );
    this.navigateBack();
  }

  onVideoFormatChange(event: MatSelectChange) {
    const videoFormat = event.value;

    this.selectedVideoType = videoFormat;
    const has1920VideoRes = this.videoResolution.find((res) => res.value === RESOLUTION_1080);

    if (videoFormat === 'sideBySide') {
      if (has1920VideoRes) {
        this.videoResolution = this.videoResolution.filter((res) => res.value !== RESOLUTION_1080);
      }

      if (this.selectedVideoResolution === RESOLUTION_1080) {
        this.selectedVideoResolution = this.videoResolution[this.videoResolution.length - 1].value;
      }
    } else if (!has1920VideoRes) {
      this.videoResolution = [
        ...this.videoResolution,
        {
          value: RESOLUTION_1080,
          text: RESOLUTION_1080,
        },
      ];
    }
  }
}
